import { motion } from "framer-motion";
import { Eyebrow, Reveal } from "./Motion";
import { STAR } from "./ZodiacWheel";

export const DEITIES = [
  { id: "ganesha", name: "Lord Ganesha", line: "Remover of obstacles, honoured at every new beginning." },
  { id: "shiva", name: "Lord Shiva", line: "Stillness, transformation and inner strength." },
  { id: "lakshmi", name: "Goddess Lakshmi", line: "Prosperity, abundance and grace in the home." },
  { id: "krishna", name: "Lord Krishna", line: "Love, devotion and wisdom of the heart." },
  { id: "hanuman", name: "Lord Hanuman", line: "Courage, protection and unwavering faith." },
  { id: "durga", name: "Goddess Durga", line: "Divine protection from negativity and fear." },
  { id: "kali", name: "Kali Mata", line: "Fierce mother who destroys evil and protects her devotees." },
];

const FAITHS = [
  { id: "islam", name: "Islamic Faith", line: "Peace, prayer and trust in the Almighty.", glyph: "☪\uFE0E" },
  { id: "jesus", name: "Jesus Christ", line: "Love, compassion and healing grace.", glyph: "✝\uFE0E" },
];

const Sparkle = ({ className }) => (
  <svg viewBox="0 0 24 24" className={`twinkle absolute h-4 w-4 text-[#F3E5AB] ${className}`} aria-hidden="true"><path d={STAR} fill="currentColor" /></svg>
);

const SacredFrame = ({ id, name, glyph = "ॐ", aspect = "aspect-[2/3]" }) => (
  <div className={`clip-arch relative ${aspect} overflow-hidden border border-[#D4AF37]/40 bg-[#0E081D]`}>
    <img src={`/gods/${id}.webp`} alt={`${name} devotional artwork`} loading="lazy"
      className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#07040D]/80 via-transparent to-[#D4AF37]/10" />
    <svg viewBox="0 0 200 200" className="spin-slow pointer-events-none absolute left-1/2 top-[6%] w-[78%] -translate-x-1/2 opacity-60 mix-blend-screen" aria-hidden="true">
      <circle cx="100" cy="100" r="96" fill="none" stroke="#F3E5AB" strokeWidth="0.6" strokeDasharray="1 5" />
      <circle cx="100" cy="100" r="86" fill="none" stroke="#D4AF37" strokeWidth="0.5" />
      {Array.from({ length: 24 }).map((_, i) => (
        <line key={i} x1="100" y1="4" x2="100" y2={i % 2 ? 10 : 14} stroke="#D4AF37" strokeWidth="0.6" transform={`rotate(${i * 15} 100 100)`} />
      ))}
    </svg>
    <div className="clip-arch pointer-events-none absolute inset-2 border border-[#F3E5AB]/30" />
    <Sparkle className="left-4 top-[22%]" />
    <Sparkle className="right-5 top-[30%] [animation-delay:1.3s]" />
    <Sparkle className="bottom-[22%] left-6 h-3 w-3 [animation-delay:2.1s]" />
    <span className="absolute bottom-3 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border border-[#D4AF37]/60 bg-[#07040D]/70 text-lg text-[#F3E5AB] backdrop-blur">{glyph}</span>
  </div>
);

const Card = ({ d, i, aspect, className = "" }) => (
  <motion.figure
    data-testid={`deity-card-${d.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay: (i % 7) * 0.07, ease: [0.22, 1, 0.36, 1] }}
    className={`group ${className}`}
  >
    <SacredFrame id={d.id} name={d.name} glyph={d.glyph} aspect={aspect} />
    <figcaption className="mt-4">
      <p className="font-display text-xl text-[#F3E5AB] sm:text-2xl">{d.name}</p>
      <p className="mt-1 text-xs leading-relaxed text-slate-300 sm:text-sm">{d.line}</p>
    </figcaption>
  </motion.figure>
);

export const DivineBlessings = () => (
  <section data-testid="divine-blessings-section" className="relative overflow-hidden px-5 py-24 sm:px-8 sm:py-32 lg:px-12">
    <div className="absolute left-1/2 top-24 h-[520px] w-[1100px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(212,175,55,0.10),transparent)]" />
    <div className="relative mx-auto max-w-[1400px]">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-6">
          <Eyebrow>Divine Blessings</Eyebrow>
          <h2 className="font-display mt-5 text-4xl leading-[1.02] text-white sm:text-5xl lg:text-6xl">
            Guided by the <span className="italic gold-text">grace of the divine</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
          <p className="text-base leading-relaxed text-slate-300">
            Every reading and healing session begins with prayer. We seek the blessings of the deities who guide our practice,
            so that each consultation is offered with devotion, humility and care.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
        {DEITIES.map((d, i) => (
          <Card key={d.id} d={d} i={i} className={i === DEITIES.length - 1 ? "col-span-2 mx-auto w-1/2 md:col-span-1 md:w-full" : ""} />
        ))}
      </div>

      <div data-testid="all-faiths-section" className="mt-20 grid items-center gap-8 border-t border-[#D4AF37]/15 pt-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow>All Faiths Welcome</Eyebrow>
          <h3 className="font-display mt-5 text-3xl leading-[1.05] text-white sm:text-5xl">
            One light, <span className="italic gold-text">many paths</span>
          </h3>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300">
            We warmly welcome clients of every faith and background — Hindu, Muslim, Christian and beyond — and honour
            each person's beliefs with the same respect and care.
          </p>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 lg:col-span-6 lg:col-start-7">
          {FAITHS.map((d, i) => <Card key={d.id} d={d} i={i} aspect="aspect-[3/4]" />)}
        </div>
      </div>
    </div>
  </section>
);
